import React, { useState, useEffect } from 'react';
import { okJson } from '../../../services/httpError';
import { X, Edit2, Loader2, LogIn, LogOut, Wifi, Copy, Check, ChevronDown, ChevronUp, Trash2, Maximize2 } from 'lucide-react';
import { fetchWithAuth, makeAbsolutePhotoUrl } from '../../../services/api';
import { cn } from '../../../utils/cn';
import { LicensePlate } from '../../ui/LicensePlate';
import { CondoType, getBlockLabel, getUnitLabel } from '../../../utils/terminology';
import { describeTag, RfidTagFormat } from '../../../utils/rfidTagFormat';
import { buildVehicleAccess, vehicleAccessSource, VehicleAccessZone } from '../../../utils/vehicleAccess';
import { VehicleAccessTab } from './VehicleAccessTab';
import { VehiclePhoto } from '../../vehicles/VehiclePhoto';

interface Person {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  profile_picture_url?: string;
  identity_photo?: { photo_url: string };
}

interface Vehicle {
  id: string;
  plate: string;
  rfid_tag?: string;
  /** How to read rfid_tag. Absent means the legacy 'auto' inference. */
  rfid_tag_format?: string;
  model?: string;
  brand?: string;
  color?: string;
  year?: number;
  owner_type?: string;
  owner_name?: string;
  person_id?: string;
  person?: Person;
  unit_id?: string;
  unit?: { id: string; number: string; block?: { id: string; name: string }; street?: { id: string; name: string }; street_number?: string };
  is_active: boolean;
  notes?: string;
  photo_url?: string;
}

interface AccessEvent {
  id: string;
  event_timestamp: string;
  event_type?: string;
  access_type?: string;
  direction?: string;
  device_name?: string;
  zone_name?: string;
  person_name?: string;
  vehicle_plate?: string;
  status?: string;
  is_authorized?: boolean;
  authorization_decision?: string;
  description?: string;
}

interface VehicleDetailModalProps {
  open: boolean;
  vehicle: Vehicle | null;
  customerId: string;
  customerType?: 'condominio' | 'empresa';
  condoType?: CondoType;
  onClose: () => void;
  onEdit: (vehicle: Vehicle) => void;
  onSync?: (vehicle: Vehicle) => void;
  isSyncing?: boolean;
  onRevoke?: (vehicle: Vehicle) => void;
  isRevoking?: boolean;
  onDelete?: (vehicle: Vehicle) => void;
  isDeleting?: boolean;
  onPersonClick?: (personId: string) => void;
  onUnitClick?: (unitId: string) => void;
  lightTheme?: boolean;
}


const OWNER_TYPE_COLORS_DARK: Record<string, string> = {
  resident: 'bg-blue-500/20 text-blue-400',
  employee: 'bg-indigo-500/20 text-indigo-400',
  visitor: 'bg-green-500/20 text-green-400',
  service_provider: 'bg-orange-500/20 text-orange-400',
  company: 'bg-gray-500/20 text-gray-400',
};

const OWNER_TYPE_COLORS_LIGHT: Record<string, string> = {
  resident: 'bg-blue-100 text-blue-700',
  employee: 'bg-indigo-100 text-indigo-700',
  visitor: 'bg-green-100 text-green-700',
  service_provider: 'bg-orange-100 text-orange-700',
  company: 'bg-gray-100 text-gray-600',
};

function getOwnerTypeLabel(ownerType: string, customerType?: 'condominio' | 'empresa'): string {
  if (ownerType === 'resident') return customerType === 'empresa' ? 'Residente' : 'Morador';
  if (ownerType === 'employee') return 'Funcionário';
  if (ownerType === 'visitor') return 'Visitante';
  if (ownerType === 'service_provider') return 'Prestador';
  if (ownerType === 'company') return 'Empresa';
  return ownerType;
}

type VehicleTab = 'geral' | 'permissoes' | 'historico';

/**
 * The same three tabs the person modal has, in the same order and with the same names.
 *
 * A vehicle and a person are the two things an operator opens all day; they were two completely
 * different screens — a person got identity, permissions and history, a vehicle got a photo and a
 * scrolling wall. Matching the shape means one mental model for both.
 */
const VEHICLE_TABS: Array<{ id: VehicleTab; label: string }> = [
  { id: 'geral', label: 'Geral' },
  { id: 'permissoes', label: 'Permissões' },
  { id: 'historico', label: 'Histórico' },
];

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export const VehicleDetailModal: React.FC<VehicleDetailModalProps> = ({
  open,
  vehicle,
  customerId,
  customerType,
  condoType,
  onClose,
  onEdit,
  onSync,
  isSyncing = false,
  onRevoke,
  isRevoking = false,
  onDelete,
  isDeleting = false,
  onPersonClick,
  onUnitClick,
  lightTheme = false,
}) => {
  const [events, setEvents] = useState<AccessEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  // Kept separate from `events` so a failed lookup is not shown as an empty history.
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [idCopied, setIdCopied] = useState(false);
  const [isIdExpanded, setIsIdExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<VehicleTab>('geral');
  const [photoZoomed, setPhotoZoomed] = useState(false);
  // A record can point at a file that is gone; say so rather than showing a broken glyph.
  // Declared with the other state: everything below the `!open || !vehicle` guard is past an
  // early return, where a hook would change the hook order between renders.
  const [photoFailed, setPhotoFailed] = useState(false);
  // A different photo deserves a fresh attempt; `vehicle?.photo_url` is read with optional
  // chaining because this runs before the `!open || !vehicle` guard below.
  const vehiclePhotoField = vehicle?.photo_url;
  useEffect(() => { setPhotoFailed(false); }, [vehiclePhotoField]);
  const [zones, setZones] = useState<VehicleAccessZone[]>([]);
  const [loadingZones, setLoadingZones] = useState(false);
  // Separate from `zones` for the same reason as `eventsError`: a failed lookup rendered as an
  // empty list reads as "this vehicle has no access", which is a different thing to act on.
  const [zonesError, setZonesError] = useState<string | null>(null);

  const accessSource = vehicleAccessSource(vehicle);

  const handleCopyId = async () => {
    if (!vehicle?.id) return;
    try {
      await navigator.clipboard.writeText(vehicle.id);
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (!open || !vehicle) {
      setEvents([]);
      setEventsError(null);
      return;
    }

    if (!vehicle.id) {
      return;
    }
    setLoadingEvents(true);
    const params = new URLSearchParams({
      customer_id: customerId,
      limit: '50',
      sort_by: 'event_timestamp',
      sort_order: 'desc',
    });
    // Send both filters so backend can OR them together:
    // vehicle_id matches RFID tag scans, vehicle_plate matches LPR reads.
    if (vehicle.id) {
      params.set('vehicle_id', vehicle.id);
    }
    if (vehicle.plate) {
      params.set('vehicle_plate', vehicle.plate);
    }
    // Must have at least one filter to avoid returning all customer events
    if (!vehicle.id && !vehicle.plate) {
      setLoadingEvents(false);
      return;
    }
    fetchWithAuth(`/api/v1/events/access?${params.toString()}`)
      .then(r => okJson<any>(r, 'Erro ao carregar o histórico de acessos'))
      .then(data => {
        setEvents(data.data || data.events || []);
        setEventsError(null);
      })
      // Distinguished from an empty history: an unchecked body used to render a rejected query
      // as "nenhum acesso registrado".
      .catch((err: any) => {
        setEvents([]);
        setEventsError(err?.message || 'Erro ao carregar o histórico de acessos');
      })
      .finally(() => setLoadingEvents(false));
  }, [open, vehicle, customerId]);

  // Reopening on another vehicle must not show the previous one's zones while the new ones load.
  useEffect(() => {
    setActiveTab('geral');
    setZones([]);
    setZonesError(null);
    setPhotoZoomed(false);
  }, [vehicle?.id]);

  // ESC closes the zoom before it closes anything else — the operator's instinct after enlarging
  // a photo is to press it, and without this the key would fall through to whatever else listens.
  useEffect(() => {
    if (!photoZoomed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setPhotoZoomed(false);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [photoZoomed]);

  // Fetched only when the tab is actually opened: this is two more round trips, and most of the
  // time the operator came to read the plate.
  useEffect(() => {
    if (!open || activeTab !== 'permissoes') return;
    if (!accessSource.queryId) return;

    const path = accessSource.kind === 'person'
      ? `/api/v1/customers/${customerId}/people/${accessSource.queryId}/effective-access-zones`
      : `/api/v1/customers/${customerId}/units/${accessSource.queryId}/effective-access-zones`;

    let cancelled = false;
    setLoadingZones(true);
    fetchWithAuth(path)
      .then(r => okJson<any>(r, 'Erro ao carregar as permissões de acesso'))
      .then(data => {
        if (cancelled) return;
        setZones(buildVehicleAccess(data.zones || []));
        setZonesError(null);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setZones([]);
        setZonesError(err?.message || 'Erro ao carregar as permissões de acesso');
      })
      .finally(() => { if (!cancelled) setLoadingZones(false); });

    return () => { cancelled = true; };
  }, [open, activeTab, customerId, accessSource.kind, accessSource.queryId]);

  if (!open || !vehicle) return null;

  const ownerName = vehicle.person
    ? `${vehicle.person.first_name} ${vehicle.person.last_name}`.trim()
    : vehicle.owner_name || null;

  const unitStreetLine = vehicle.unit?.street?.name
    ? (vehicle.unit.street_number ? `${vehicle.unit.street.name}, ${vehicle.unit.street_number}` : vehicle.unit.street.name)
    : null;
  // Quadra and lote on their OWN lines, not joined by formatUnitLabel's " – ".
  //
  // In a loteamento the two together run to "Bloco Q16 – Unidade Q16LT18 - Rua Jefferson ...",
  // which has no chance of fitting a 290px column: it truncated to "…- Ru…" and neither half was
  // readable. Separately, each is short enough to read whole.
  const unitBlockLine = vehicle.unit?.block?.name
    ? `${getBlockLabel(condoType)} ${vehicle.unit.block.name}`
    : null;
  const unitNumberLine = vehicle.unit?.number
    ? `${getUnitLabel(condoType)} ${vehicle.unit.number}`
    : null;

  const photoUrl = vehicle.photo_url ? makeAbsolutePhotoUrl(vehicle.photo_url) : null;

  const personPhotoUrl = vehicle.person
    ? makeAbsolutePhotoUrl(vehicle.person.profile_picture_url || vehicle.person.identity_photo?.photo_url || '')
    : null;

  const ownerTypeColors = lightTheme ? OWNER_TYPE_COLORS_LIGHT : OWNER_TYPE_COLORS_DARK;

  const ownerTypeColor =
    vehicle.owner_type === 'resident' ? (lightTheme ? 'bg-blue-100 text-blue-700' : 'bg-blue-500/15 text-blue-400') :
    vehicle.owner_type === 'employee' ? (lightTheme ? 'bg-indigo-100 text-indigo-700' : 'bg-indigo-500/15 text-indigo-400') :
    vehicle.owner_type === 'visitor' ? (lightTheme ? 'bg-green-100 text-green-700' : 'bg-green-500/15 text-green-400') :
    vehicle.owner_type === 'service_provider' ? (lightTheme ? 'bg-orange-100 text-orange-700' : 'bg-orange-500/15 text-orange-400') :
    (lightTheme ? 'bg-gray-100 text-gray-500' : 'bg-gray-800 text-gray-400');

  const chipActive = cn('text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider',
    vehicle.is_active
      ? (lightTheme ? 'bg-green-100 text-green-700' : 'bg-green-500/15 text-green-400')
      : (lightTheme ? 'bg-gray-100 text-gray-500' : 'bg-gray-800 text-gray-500'));
  const cardCls = cn('rounded-xl border p-3', lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-900/50 border-gray-800');
  const labelCls = cn('text-[10px] font-bold uppercase tracking-widest mb-1.5', lightTheme ? 'text-gray-400' : 'text-gray-600');

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        className={cn(
          "rounded-2xl max-w-4xl w-full max-h-[90vh] flex shadow-2xl border overflow-hidden",
          lightTheme ? "bg-white border-gray-200" : "bg-[#111113] border-gray-800"
        )}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Left: identity. Same shape as the person modal — photo, who it is, what you can do
             about it, then the tab nav — so the two screens read as one product. ── */}
        <aside className={cn("w-[290px] flex-shrink-0 flex flex-col border-r overflow-y-auto",
          lightTheme ? "border-gray-100" : "border-gray-800/70")}>
          <div className="p-4 space-y-3">
            {/* A vehicle photo is read as evidence — the operator is checking whether the car at
                the gate is this car — and 290px of sidebar is not enough to do that. Clickable
                only when there IS a photo: a button that opens nothing is worse than a plain box. */}
            <div className={cn("relative rounded-xl overflow-hidden h-[150px]",
              lightTheme ? "bg-gray-100 border border-gray-200" : "bg-gray-900/60 border border-gray-800")}>
              {photoUrl && !photoFailed
                ? <button
                    type="button"
                    onClick={() => setPhotoZoomed(true)}
                    className="group block w-full h-full cursor-zoom-in"
                    title="Ampliar foto"
                    aria-label="Ampliar foto do veículo"
                  >
                    {/* A stored URL can 404 — an object goes missing, a record outlives its
                        file. The list falls back to a labelled box; without the same here the
                        operator gets the browser's broken-image glyph and reads it as data loss. */}
                    <VehiclePhoto src={photoUrl} alt={vehicle.plate} onError={() => setPhotoFailed(true)} />
                    <span className="absolute bottom-1.5 right-1.5 p-1 rounded-md bg-black/55 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <Maximize2 className="h-3 w-3" />
                    </span>
                  </button>
                : <div className="absolute inset-0 flex items-center justify-center">
                    <span className={cn("text-[10px] font-bold tracking-[0.3em] uppercase select-none", lightTheme ? "text-gray-400" : "text-gray-600")}>
                      {photoUrl ? 'FOTO INDISPONÍVEL' : 'SEM FOTO'}
                    </span>
                  </div>
              }
            </div>

            <LicensePlate plate={vehicle.plate} size="md" />

            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={chipActive}>{vehicle.is_active ? 'ATIVO' : 'INATIVO'}</span>
              {vehicle.owner_type && (
                <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider', ownerTypeColor)}>
                  {getOwnerTypeLabel(vehicle.owner_type, customerType).toUpperCase()}
                </span>
              )}
            </div>

            {(vehicle.brand || vehicle.model || vehicle.color || vehicle.year) && (
              <p className={cn("text-xs", lightTheme ? "text-gray-500" : "text-gray-500")}>
                {[vehicle.brand, vehicle.model, vehicle.color, vehicle.year ? String(vehicle.year) : null].filter(Boolean).join(' · ')}
              </p>
            )}

            {ownerName && (
              <div className="flex items-center gap-2">
                <div className={cn("flex-shrink-0 w-8 h-8 rounded-full overflow-hidden border", lightTheme ? "border-gray-200 bg-gray-100" : "border-gray-700 bg-gray-800")}>
                  {personPhotoUrl
                    ? <img src={personPhotoUrl} alt={ownerName} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center">
                        <span className={cn("text-[10px] font-bold", lightTheme ? "text-gray-400" : "text-gray-600")}>
                          {ownerName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                  }
                </div>
                <div className="min-w-0 flex-1">
                  {vehicle.person_id && onPersonClick
                    ? <button onClick={() => { onPersonClick(vehicle.person_id!); onClose(); }}
                        className={cn("text-sm font-semibold hover:underline text-left leading-tight truncate block", lightTheme ? "text-gray-900" : "text-white")}>
                        {ownerName}
                      </button>
                    : <p className={cn("text-sm font-semibold leading-tight truncate", lightTheme ? "text-gray-900" : "text-white")}>{ownerName}</p>
                  }
                  {/* One line per row, each truncating on its own. The address and the
                      quadra/lote are long in a loteamento ("Bloco Q16 - Unidade Q16LT18 - Rua
                      ..."), and run together they pushed past the sidebar and were cut off
                      mid-word with no ellipsis. `w-full min-w-0` is what makes `truncate` work
                      here: without it the block sizes to its content and never shrinks. The full
                      text stays available on hover. */}
                  {(unitStreetLine || unitBlockLine || unitNumberLine) && (
                    (vehicle.unit_id || vehicle.unit?.id) && onUnitClick
                      ? <button onClick={() => { onUnitClick((vehicle.unit_id || vehicle.unit!.id)!); onClose(); }}
                          className="block w-full min-w-0 text-left mt-0.5 group">
                          {unitStreetLine && <p title={unitStreetLine} className={cn("text-xs font-medium truncate group-hover:underline", lightTheme ? "text-blue-600" : "text-blue-400")}>{unitStreetLine}</p>}
                          {unitBlockLine && <p title={unitBlockLine} className={cn("text-xs truncate group-hover:underline", unitStreetLine ? (lightTheme ? "text-gray-500" : "text-gray-400") : (lightTheme ? "text-blue-600" : "text-blue-400"))}>{unitBlockLine}</p>}
                          {unitNumberLine && <p title={unitNumberLine} className={cn("text-xs truncate group-hover:underline", lightTheme ? "text-gray-500" : "text-gray-400")}>{unitNumberLine}</p>}
                        </button>
                      : <div className="w-full min-w-0 mt-0.5">
                          {unitStreetLine && <p title={unitStreetLine} className={cn("text-xs font-medium truncate", lightTheme ? "text-gray-700" : "text-gray-300")}>{unitStreetLine}</p>}
                          {unitBlockLine && <p title={unitBlockLine} className={cn("text-xs truncate", lightTheme ? "text-gray-500" : "text-gray-400")}>{unitBlockLine}</p>}
                          {unitNumberLine && <p title={unitNumberLine} className={cn("text-xs truncate", lightTheme ? "text-gray-500" : "text-gray-400")}>{unitNumberLine}</p>}
                        </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <nav className={cn("border-t mt-1", lightTheme ? "border-gray-100" : "border-gray-800")}>
            {/* Same type as the person modal's sidebar: all-caps label, no icon. A vehicle and
                a person are the two things an operator opens all day — two tab menus in two
                different typographies is two products. */}
            {VEHICLE_TABS.map(tab => {
              const isActive = activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={cn("w-full text-left px-4 py-2.5 text-[11px] font-bold tracking-widest uppercase transition-all border-l-2",
                    isActive
                      ? (lightTheme ? "border-gray-900 text-gray-900 bg-gray-50" : "border-blue-500 text-white bg-gray-800/60")
                      : (lightTheme ? "border-transparent text-gray-400 hover:text-gray-700 hover:bg-gray-50/70" : "border-transparent text-gray-500 hover:text-gray-300 hover:bg-gray-800/30")
                  )}>
                  {tab.label}
                </button>
              );
            })}
          </nav>

          {/* Actions live at the FOOT of the sidebar, the way the person modal places them: one
              fixed spot, below the navigation, reachable from whichever tab is open. mt-auto pins
              them to the bottom when the identity block is short rather than leaving them floating
              mid-column. */}
          {(onSync || onRevoke) && (
            <div className={cn("mt-auto border-t p-3 flex items-center gap-1.5",
              lightTheme ? "border-gray-100" : "border-gray-800")}>
              {/* A matched pair, identical to the person modal's: same shape, size and weight,
                  differing only in accent, and no icons — the label carries it. */}
              {onSync && (
                <button onClick={() => onSync(vehicle)} disabled={isSyncing}
                  className={cn("flex-1 px-2 py-1.5 text-[10px] font-bold tracking-widest uppercase rounded-lg border transition-colors disabled:opacity-50",
                    lightTheme ? "bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200" : "bg-blue-900/20 hover:bg-blue-900/40 text-blue-400 border-blue-900/40"
                  )}>
                  {isSyncing ? '...' : 'Sincronizar'}
                </button>
              )}
              {onRevoke && (
                <button onClick={() => onRevoke(vehicle)} disabled={isRevoking}
                  className={cn("flex-1 px-2 py-1.5 text-[10px] font-bold tracking-widest uppercase rounded-lg border transition-colors disabled:opacity-50",
                    lightTheme ? "bg-red-50 hover:bg-red-100 text-red-600 border-red-200" : "bg-red-900/20 hover:bg-red-900/40 text-red-400 border-red-900/40"
                  )}>
                  {isRevoking ? '...' : 'Revogar'}
                </button>
              )}
            </div>
          )}
        </aside>

        {/* ── Right: the tab's content ── */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className={cn("flex items-center justify-between px-4 py-2.5 flex-shrink-0 border-b", lightTheme ? "border-gray-100" : "border-gray-800/70")}>
            <div className="flex items-center gap-1.5 min-w-0">
              <span className={cn("text-[10px] font-bold tracking-[0.3em] uppercase flex-shrink-0", lightTheme ? "text-gray-400" : "text-gray-600")}>Veículo</span>
              {vehicle.id && (
                <>
                  <div className={cn("flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-mono", lightTheme ? "bg-gray-100 text-gray-500" : "bg-gray-800/60 text-gray-500")}>
                    <button onClick={() => setIsIdExpanded(!isIdExpanded)} className="hover:opacity-70">
                      <span className={cn("block", !isIdExpanded && "truncate max-w-[72px]")}>{vehicle.id}</span>
                    </button>
                    {!isIdExpanded
                      ? <ChevronDown className="h-2.5 w-2.5 flex-shrink-0 cursor-pointer hover:opacity-70" onClick={() => setIsIdExpanded(true)} />
                      : <ChevronUp className="h-2.5 w-2.5 flex-shrink-0 cursor-pointer hover:opacity-70" onClick={() => setIsIdExpanded(false)} />
                    }
                  </div>
                  <button onClick={handleCopyId} className={cn("p-1 rounded transition-all flex-shrink-0", idCopied ? (lightTheme ? "bg-green-100 text-green-600" : "bg-green-900/30 text-green-400") : (lightTheme ? "bg-gray-100 text-gray-500 hover:bg-gray-200" : "bg-gray-800/60 text-gray-500 hover:bg-gray-700/60"))} title={idCopied ? "Copiado!" : "Copiar ID"}>
                    {idCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </button>
                </>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => onEdit(vehicle)} title="Editar"
                className={cn("p-1.5 rounded-lg transition-colors", lightTheme ? "text-gray-400 hover:text-gray-700 hover:bg-gray-100" : "text-gray-500 hover:text-gray-200 hover:bg-gray-800")}>
                <Edit2 className="h-3.5 w-3.5" />
              </button>
              {onDelete && (
                <button onClick={() => onDelete(vehicle)} disabled={isDeleting} title="Excluir"
                  className={cn("p-1.5 rounded-lg transition-colors disabled:opacity-50", lightTheme ? "text-gray-400 hover:text-red-600 hover:bg-red-50" : "text-gray-500 hover:text-red-400 hover:bg-red-500/10")}>
                  <Trash2 className={cn("h-3.5 w-3.5", isDeleting && "animate-pulse")} />
                </button>
              )}
              <div className={cn("w-px h-4 mx-0.5", lightTheme ? "bg-gray-200" : "bg-gray-800")} />
              <button onClick={onClose} className={cn("p-1.5 rounded-lg transition-colors", lightTheme ? "text-gray-400 hover:text-gray-600 hover:bg-gray-100" : "text-gray-600 hover:text-gray-300 hover:bg-gray-800")}>
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0 p-4">
            {activeTab === 'geral' && (
              <div className="space-y-3">
                {vehicle.rfid_tag ? (() => {
                  const fmt = describeTag(vehicle.rfid_tag, (vehicle.rfid_tag_format as RfidTagFormat) || 'auto');
                  return (
                    <div className={cardCls}>
                      <p className={labelCls}>RFID</p>
                      <span className={cn("font-mono text-sm font-semibold", lightTheme ? "text-gray-800" : "text-gray-200")}>{vehicle.rfid_tag}</span>
                      {fmt && (
                        <div className={cn("flex gap-3 flex-wrap text-[11px] mt-1.5", lightTheme ? "text-gray-500" : "text-gray-500")}>
                          <span><span className="opacity-50">HEX</span> {fmt.hex}</span>
                          <span><span className="opacity-50">DEC</span> {fmt.dec}</span>
                          <span><span className="opacity-50">WG</span> {fmt.wg}</span>
                          <span><span className="opacity-50">ID</span> {fmt.id}</span>
                        </div>
                      )}
                    </div>
                  );
                })() : (
                  // Stated rather than omitted: no tag is the reason an RFID reader will never
                  // recognise this car, and an absent section looks like a loading failure.
                  <div className={cardCls}>
                    <p className={labelCls}>RFID</p>
                    <p className={cn("text-sm", lightTheme ? "text-gray-500" : "text-gray-500")}>
                      Sem tag cadastrada — o veículo só pode ser identificado por câmera LPR.
                    </p>
                  </div>
                )}

                <div className={cardCls}>
                  <p className={labelCls}>Identificação</p>
                  <div className="grid grid-cols-2 gap-3">
                    <InfoRow label="Placa" value={vehicle.plate} mono lightTheme={lightTheme} />
                    <InfoRow label="Marca" value={vehicle.brand || '—'} lightTheme={lightTheme} />
                    <InfoRow label="Modelo" value={vehicle.model || '—'} lightTheme={lightTheme} />
                    <InfoRow label="Cor" value={vehicle.color || '—'} lightTheme={lightTheme} />
                    <InfoRow label="Ano" value={vehicle.year ? String(vehicle.year) : '—'} lightTheme={lightTheme} />
                    <InfoRow label="Responsável" value={ownerName || '—'} lightTheme={lightTheme} />
                  </div>
                </div>

                {vehicle.notes && (
                  <div className={cardCls}>
                    <p className={labelCls}>Observações</p>
                    <p className={cn("text-sm", lightTheme ? "text-gray-700" : "text-gray-300")}>{vehicle.notes}</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'permissoes' && (
              <VehicleAccessTab
                source={accessSource}
                zones={zones}
                loading={loadingZones}
                error={zonesError}
                lightTheme={lightTheme}
              />
            )}

            {activeTab === 'historico' && (
              loadingEvents ? (
                <div className="flex justify-center py-8">
                  <Loader2 className={cn("h-5 w-5 animate-spin", lightTheme ? "text-gray-400" : "text-gray-500")} />
                </div>
              ) : eventsError ? (
                <p className={cn("text-xs px-3 py-4 text-center", lightTheme ? "text-red-600" : "text-red-400")}>{eventsError}</p>
              ) : events.length === 0 ? (
                <p className={cn("text-sm px-3 py-6 text-center", lightTheme ? "text-gray-500" : "text-gray-500")}>
                  Nenhum acesso registrado para este veículo.
                </p>
              ) : (
                <div className={cn("rounded-xl border overflow-hidden", lightTheme ? "border-gray-200" : "border-gray-800")}>
                  {events.map(ev => {
                    const isEntry = ev.direction === 'entrada';
                    const isExit = ev.direction === 'saida';
                    const isDenied = ev.is_authorized === false || ev.authorization_decision === 'deny' || ev.event_type === 'access_denied';
                    const isSuccess = !isDenied;
                    return (
                      <div key={ev.id} className={cn("flex items-start gap-2.5 px-3 py-2.5 border-b last:border-0", lightTheme ? "border-gray-100" : "border-gray-800/60")}>
                        <div className={cn('mt-0.5 h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0',
                          isEntry ? (lightTheme ? 'bg-green-100' : 'bg-green-500/15') :
                          isExit ? (lightTheme ? 'bg-blue-100' : 'bg-blue-500/15') :
                          (lightTheme ? 'bg-gray-100' : 'bg-gray-800')
                        )}>
                          {isEntry ? <LogIn className={cn("h-3 w-3", lightTheme ? "text-green-600" : "text-green-400")} />
                            : isExit ? <LogOut className={cn("h-3 w-3", lightTheme ? "text-blue-600" : "text-blue-400")} />
                            : <Wifi className={cn("h-3 w-3", lightTheme ? "text-gray-400" : "text-gray-500")} />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={cn('text-xs font-semibold', isSuccess ? (lightTheme ? 'text-gray-800' : 'text-gray-200') : (lightTheme ? 'text-red-600' : 'text-red-400'))}>
                              {isEntry ? 'Entrada' : isExit ? 'Saída' : ev.direction || 'Acesso'}
                            </span>
                            {!isSuccess && <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded-full', lightTheme ? 'bg-red-100 text-red-600' : 'bg-red-500/15 text-red-400')}>Negado</span>}
                          </div>
                          <p className={cn("text-xs mt-0.5 truncate", lightTheme ? "text-gray-500" : "text-gray-500")}>
                            {ev.device_name || ev.zone_name || ev.description || '—'}
                          </p>
                        </div>
                        <span className={cn("text-[10px] flex-shrink-0 mt-0.5 tabular-nums", lightTheme ? "text-gray-400" : "text-gray-600")}>
                          {formatTimestamp(ev.event_timestamp)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* Zoom. Rendered above the modal and closing on the backdrop or ESC, matching the person
          modal's photo viewer so the two behave the same way. */}
      {photoZoomed && photoUrl && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/90 p-4"
          onClick={(e) => { e.stopPropagation(); setPhotoZoomed(false); }}
        >
          <div className="relative max-w-[85vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPhotoZoomed(false)}
              className="absolute top-3 right-3 z-10 p-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shadow-lg"
              aria-label="Fechar visualizador de foto"
              title="Fechar (ESC)"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={photoUrl}
              alt={vehicle.plate}
              className="max-w-[85vw] max-h-[85vh] object-contain rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode; mono?: boolean; lightTheme?: boolean }> = ({ label, value, mono, lightTheme }) => (
  <div>
    <p className={cn("text-xs mb-0.5", lightTheme ? "text-gray-500" : "text-gray-500")}>{label}</p>
    {typeof value === 'string' ? (
      <p className={cn('text-sm', mono && 'font-mono tracking-wider', lightTheme ? 'text-gray-900' : 'text-gray-200')}>{value}</p>
    ) : (
      value
    )}
  </div>
);
